import { authService } from "@/services/AuthService";
import { EmojiPicker } from "./EmojiPicker";

/**
 * Replier —— 浮动回复表单组件
 *
 * 职责：管理单例浮动表单的完整生命周期：
 * - 移动到目标评论下方并显示
 * - 隐藏并重置内容
 * - 提交前移回安全区域（避免被 Unpoly 片段替换销毁）
 * - 初始化 Emoji 选择器（只初始化一次）
 * - 未登录时拦截提交，执行登录 + 回复评论
 * - 已登录时由 Unpoly 处理提交
 */
export class Replier {
  private element: HTMLFormElement | null = null;
  private textarea: HTMLTextAreaElement | null = null;
  private parentIdInput: HTMLInputElement | null = null;
  private mentionEl: HTMLElement | null = null;
  private emojiPicker: EmojiPicker | null = null;
  private formSelector: string;
  private emojiPickerSelector: string;
  private upTarget: string | undefined = undefined;

  constructor(
    formSelector: string,
    emojiPickerSelector: string = ".emoji-picker",
  ) {
    this.formSelector = formSelector;
    this.emojiPickerSelector = emojiPickerSelector;

    // 监听评论区就绪事件，重绑定元素
    up.on("comments:ready", () => {
      this.initial();
    });

    // 回复按钮：将表单移动到目标评论下方
    up.on(
      "click",
      "#comments .reply",
      (_event: Event, _element: Element, options: any) => {
        this.moveTo(options.commentId, options.replyUsername);
      },
    );

    // 回复表单取消按钮
    up.on("click", `${this.formSelector} .cancel`, () => {
      this.cancel();
    });
  }

  submit = (event: up.FormSubmitEvent) => {
    const { parentId: parentIdStr } = event.params.toObject() as {
      content: string;
      parentId: string;
    };
    const parentId = parseInt(parentIdStr, 10);

    if (authService.isSignedIn) {
      // 已登录：注入渲染配置，让 Unpoly 继续默认提交
      event.renderOptions.target = this.upTarget;
      event.renderOptions.onFinished = (result: up.RenderResult) => {
        this.cancel();
        up.emit("comments:reply", {
          parent: document.querySelector(`#comment-${parentId}`),
          element: result.fragments.at(0),
        });
        this.element?.reset();
      };
      return;
    }

    // 未登录：拦截提交，弹出登录 Modal
    event.preventDefault();
    authService.signin({
      onAccepted: () => {
        this.perform(parentId);
      },
    });
  };

  /**
   * 登录完成后：使用 up.render 发送回复请求
   * 绕过事件拦截系统，语义更清晰
   */
  private perform(parentId: number): void {
    if (!this.element) return;
    up.render({
      url: this.element.action,
      method: this.element.method,
      params: up.Params.fromForm(this.element),
      target: this.upTarget!,
      layer: "root",
      onFinished: (result: up.RenderResult) => {
        this.cancel();
        up.emit("comments:reply", {
          parent: document.querySelector(`#comment-${parentId}`),
          element: result.fragments.at(0),
        });
        this.element?.reset();
      },
    });
  }

  /**
   * 重新绑定 DOM 元素
   * 当 Unpoly 片段更新导致旧元素被销毁时，需要调用此方法重新获取新元素
   */
  initial(): void {
    this.element = document.querySelector(this.formSelector) as HTMLFormElement;
    this.textarea =
      this.element.querySelector<HTMLTextAreaElement>(".textarea")!;
    this.parentIdInput =
      this.element.querySelector<HTMLInputElement>("[name=parentId]")!;
    this.mentionEl = this.element.querySelector<HTMLElement>(".mention")!;

    // 浮动回复表单提交前：移回安全区域，防止被 Unpoly 片段替换销毁
    up.off(this.element, "up:form:submit", this.submit);
    up.on(this.element, "up:form:submit", this.submit);

    const emojiContainer = this.element.querySelector<HTMLElement>(
      this.emojiPickerSelector,
    );
    if (emojiContainer) {
      this.emojiPicker = new EmojiPicker(emojiContainer, this.textarea!);
    }
  }

  /**
   * 将表单移动到目标评论的 reply-slot 中并显示
   * 若表单已在该位置且可见，则收起（toggle 行为）
   */
  moveTo(commentId: string, username: string): void {
    if (!this.element) {
      // 这里的兜底：如果 element 丢失且 moveTo 被触发，尝试最后一次 rebind
      this.initial();
      if (!this.element) return;
    }

    const targetSlot = document.querySelector(`#reply-slot-${commentId}`);
    if (!targetSlot) return;

    // toggle：已在目标位置且可见 → 收起
    const isAlreadyHere =
      this.element.parentElement === targetSlot &&
      !this.element.classList.contains("hidden");

    if (isAlreadyHere) {
      this.hide();
      return;
    }

    // 移动到目标 slot，作为第一个子元素追加
    targetSlot.prepend(this.element);

    // 更新表单参数
    if (this.parentIdInput) this.parentIdInput.value = commentId;
    if (this.mentionEl) this.mentionEl.textContent = `@${username}`;
    this.upTarget = `#reply-slot-${commentId}:before`; // if (this.formEl) this.formEl.setAttribute('up-target', `#reply-slot-${commentId}`)

    // 显示并聚焦
    this.element.classList.remove("hidden");
    if (this.textarea) {
      this.textarea.value = "";
      this.textarea.focus();
      this.textarea.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * 隐藏表单并清空内容
   */
  hide(): void {
    if (!this.element) return;
    this.element.classList.add("hidden");
    if (this.textarea) this.textarea.value = "";
  }

  /**
   * 取消后将表单移回 #comments 安全区域并清空内容
   */
  cancel(): void {
    this.hide();
    const commentsEl = document.getElementById("comments");
    if (commentsEl && this.element) {
      commentsEl.appendChild(this.element);
    }
  }

  destroy(): void {
    this.emojiPicker?.destroy();
    this.emojiPicker = null;
    this.element = null;
    this.textarea = null;
    this.parentIdInput = null;
    this.mentionEl = null;
  }

  static compiler(selector: string, emojiPickerSelector?: string): void {
    new Replier(selector, emojiPickerSelector);
  }
}
